import { useState, useEffect } from 'react';
import { remove, union, filter, uniqWith } from 'lodash';
import Select from 'components/molecules/Select';

import { DiscoverItemType, KitResource, Kits, Templates } from 'utils/gloo-types';
import { useThunkDispatch } from 'utils/reduxHook';
import { getAllKits, getAllTemplates } from 'actions/requests';
import { discoverItemCardForKit, discoverItemCardForTemplate, kitResourceSelectOptions } from 'utils/discover';
import { SelectOption } from 'utils/frontendTypes';
import Button from 'components/atoms/Button';
import { useIsMountedRef } from 'utils/hooks';
import DiscoverCardPreview from '../DiscoverCardPreview';

interface KitResourcesListUpdatedProps {
  handleKitResourcesUpdated: (kitResources: KitResource[]) => void;
  kitResources: KitResource[];
  kitId: string;
}

function KitAuthoringResourcesList({ kitResources, handleKitResourcesUpdated, kitId }: KitResourcesListUpdatedProps) {
  const dispatch = useThunkDispatch();
  const [allKitResources, setAllKitResources] = useState<KitResource[]>([]);
  const isMountedRef = useIsMountedRef();

  const handleKitResourceSelected = (kitResourceToAdd: SelectOption) => {
    handleKitResourcesUpdated(
      uniqWith([...(kitResources || []), kitResourceToAdd.value], (a, b) => {
        if (a.type !== b.type) return false;
        switch (a.type) {
          case DiscoverItemType.KIT:
            return a.kit.id === b.kit.id;
          case DiscoverItemType.TEMPLATE:
            return a.template.id === b.template.id;
          default:
            return false;
        }
      }),
    );
  };

  const handleKitResourceRemoved = (kitResourceIndexToRemove: number) => {
    handleKitResourcesUpdated(
      filter(kitResources, (_, kitResourceIndex) => kitResourceIndex !== kitResourceIndexToRemove),
    );
  };

  const handleKitResourcePromoted = (kitResourceIndexToPromote: number) => {
    const kitResource = kitResources.splice(kitResourceIndexToPromote, 1)[0];
    kitResources.splice(kitResourceIndexToPromote + 1, 0, kitResource);
    handleKitResourcesUpdated(kitResources);
  };

  const handleKitResourceDemoted = (kitResourceIndexToDemote: number) => {
    const kitResource = kitResources.splice(kitResourceIndexToDemote, 1)[0];
    kitResources.splice(kitResourceIndexToDemote - 1, 0, kitResource);
    handleKitResourcesUpdated(kitResources);
  };

  const kitResourceForKit = (kit: Kits): KitResource => {
    return {
      type: DiscoverItemType.KIT,
      kit,
    };
  };

  const kitResourceForTemplate = (template: Templates): KitResource => {
    return {
      type: DiscoverItemType.TEMPLATE,
      template,
    };
  };

  const discoverItemForKitResource = (kitResource: KitResource) => {
    switch (kitResource.type) {
      case DiscoverItemType.KIT:
        return discoverItemCardForKit(kitResource.kit);
      case DiscoverItemType.TEMPLATE:
        return discoverItemCardForTemplate(kitResource.template);
    }
  };

  useEffect(() => {
    const loadTemplatesAndOtherKits = async () => {
      const allTemplates = await dispatch(getAllTemplates());
      const allKits = await dispatch(getAllKits());
      remove(allKits, { id: kitId });
      if (isMountedRef.current) {
        setAllKitResources(
          union<KitResource>(
            allKits.map((kit) => kitResourceForKit(kit)),
            allTemplates.map((template) => kitResourceForTemplate(template)),
          ),
        );
      }
    };
    loadTemplatesAndOtherKits();
  }, [isMountedRef]);

  return (
    <>
      <Select
        dataDurableId="kitResourcesListSelectId"
        label="Add Resource"
        options={kitResourceSelectOptions(allKitResources)}
        onChange={handleKitResourceSelected}
        placeholder="Choose Resource"
        className="mb-4"
      />
      <div className="flex flex-row flex-wrap">
        {kitResources.map((kitResource, i) => {
          const item = discoverItemForKitResource(kitResource);
          if (!item) return undefined;
          return (
            <div key={i} className="relative w-full px-2 pb-8 md:w-1/2 lg:w-1/3 xl:w-1/4 2xl:w-1/5">
              <DiscoverCardPreview
                key={i}
                itemCard={item}
                className="w-full h-full"
                onClick={() => {
                  // Noop
                }}
              />
              <div className="flex flex-row absolute top-1 right-3">
                <Button
                  onClick={() => handleKitResourceDemoted(i)}
                  icon="arrow-left"
                  dataDurableId="kitResourceDemoteButton"
                  size="xs"
                  type="white"
                  buttonType="button"
                  hideHighlight
                  disabled={!i}
                />
                <Button
                  onClick={() => handleKitResourcePromoted(i)}
                  icon="arrow-right"
                  dataDurableId="kitResourcePromoteeButton"
                  size="xs"
                  type="white"
                  buttonType="button"
                  hideHighlight
                  className="mx-1"
                  disabled={i >= kitResources.length - 1}
                />
                <Button
                  onClick={() => handleKitResourceRemoved(i)}
                  icon="trash"
                  dataDurableId="kitResourceRemoveButton"
                  size="xs"
                  type="destruction"
                  hideHighlight
                  buttonType="button"
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default KitAuthoringResourcesList;
